import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NotificationMessage } from '@models/notificationMessage.class';
import { OperationError } from '@models/operationError.class';

import { IndividualConfig, ToastrService } from 'ngx-toastr';
import { CustomDialogComponent } from 'src/components/common/customDialog/customDialog.component';

@Injectable()
export class NotificationService {
  private toastOptions: Partial<IndividualConfig> = {
    // animate: 'flyRight',
    positionClass: 'toast-top-center',
    toastClass: 'toast',
    closeButton: true,
    timeOut: 4000,
  };

  constructor(private toastr: ToastrService, private dialog: MatDialog) {}

  showSuccess(notificationMessage: NotificationMessage): void {
    const { message, title, options } =
      this.getFormattedMessage(notificationMessage);

    this.toastr.success(message, title, {
      ...this.toastOptions,
      ...options,
    });
  }

  showError(notificationMessage: NotificationMessage): void {
    if (
      !notificationMessage.message ||
      notificationMessage.message === 'undefined'
    ) {
      return;
    }
    notificationMessage.eventId =
      notificationMessage.eventId !== undefined
        ? notificationMessage.eventId
        : -1;
    console.error(
      `Error no. ${notificationMessage.eventId}`,
      notificationMessage.message,
    );

    const { message, options, eventId } =
      this.getFormattedMessage(notificationMessage);

    this.toastr.error(
      `${message} ${eventId === -1 ? '' : eventId}`,
      `Error no. ${eventId}`,
      {
        ...this.toastOptions,
        ...options,
      },
    );
  }

  showServiceError(error: OperationError): void {
    const notificationMessage: NotificationMessage = new NotificationMessage(
      error.message,
      error.eventId,
      error.isBusinessError,
    );

    if (notificationMessage.isBusinessError) {
      const data: any = {
        title: error.title,
        content: `<span>${notificationMessage.message}</span>`,
        button1: {
          label: 'Entendido',
          value: 'value',
        },
      };

      console.error(
        `Error no. ${notificationMessage.eventId}`,
        notificationMessage.message,
      );

      this.showCustomDialog(data);
    } else {
      this.showError(notificationMessage);
    }
  }

  showWarning(notificationMessage: NotificationMessage): void {
    const { message, title, options } =
      this.getFormattedMessage(notificationMessage);

    this.toastr.warning(message, title, {
      ...this.toastOptions,
      ...options,
    });
  }

  showInfo(notificationMessage: NotificationMessage): void {
    const { message, title, options } =
      this.getFormattedMessage(notificationMessage);

    this.toastr.info(message, title, {
      ...this.toastOptions,
      ...options,
    });
  }

  /**
   * Use with care. This (may have) caused performance issues in Chrome.
   *
   * Last time it was used, it blocked rendering, kept skeletons active throughout
   * the whole page, froze the app, and then the browser.
   *
   * Chrome version 89.0.4389.128
   * @deprecated
   */
  showCustom(body: string): void {
    const options: Partial<IndividualConfig> = {
      ...this.toastOptions,
      enableHtml: true,
    };

    this.toastr.show(body, undefined, options);
  }

  private showCustomDialog(data: any): Promise<any> {
    const dialogRef: any = this.dialog.open(CustomDialogComponent, {
      disableClose: true,
      data,
    });

    return dialogRef.afterClosed().toPromise();
  }

  private getFormattedMessage(notificationMessage: NotificationMessage) {
    if (notificationMessage.options && notificationMessage.options.enableHtml) {
      return {
        ...notificationMessage,
        message: this.wrapMessageInHtmlTags(notificationMessage),
      };
    }

    return notificationMessage;
  }

  private wrapMessageInHtmlTags({ message }: NotificationMessage): string {
    return `<p> ${message} </p>`;
  }
}
